// ============================================================================
// ORDERS · Collection — Repeats v2: the row's derived facts (C6·3, Conv #61)
// ============================================================================
// Pure functions over ONE repeat object as collection_repeats() answers it.
// Every rule here is read off the approved prototype
// (docs/prototypes/2026-09-22-collection-repeats-proto-v2.html): its swatch()
// decides the tile states, its add() the per-row totals, its renderStats() the
// six tiles, its flagHas()/passes() the filter flags, its SORTS/sorted() the
// order. Where the C6·3 brief and the prototype disagreed the prototype won —
// the report names each one. No JSX, no DOM: tested in repeatTileModel.test.js.
// ============================================================================

import { realBarcode, repeatPhotoBarcode } from './collectionPhotoModel';
const num = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const n0 = (v) => num(v) || 0;
const bc = (v) => (v == null ? '' : String(v));
const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));

/** The Desk's ordered quantity for a barcode — an option never counts (D5), and a colourway
 *  carrying no quantity is not an order: it answers null, so the tile reads `miss`, not `0`. */
export function deskQty(repeat, barcode) {
  const rows = Array.isArray(repeat?.desk_colourways) ? repeat.desk_colourways : [];
  const hit = rows.find((d) => d && !d.is_option && bc(d.barcode) !== '' && bc(d.barcode) === bc(barcode));
  return hit && num(hit.qty) ? num(hit.qty) : null;
}
/** True once a PI has landed with per-colour quantities (the prototype's orderedPerColour). */
export const orderedPerColour = (r) => (Array.isArray(r?.desk_colourways) ? r.desk_colourways : []).some((d) => d && !d.is_option && bc(d.barcode) !== '');

/** Σ planned over the email's kept colours PLUS the new colours — the prototype's plannedQty. */
export const plannedQty = (r) => n0(r?.planned_qty) + n0(r?.new_qty);
export const orderedQty = (r) => num(r?.ordered_qty);
/** ordered − planned, only when both sides exist (the prototype's gapOf). */
export function gapOf(r) {
  const o = n0(r?.ordered_qty), p = plannedQty(r);
  return o > 0 && p > 0 ? o - p : null;
}
/** Planned colours that no landed PI ordered — the status column's red note. */
export function missingColours(r) {
  if (!(n0(r?.ordered_qty) > 0) || !orderedPerColour(r)) return 0;
  return (Array.isArray(r?.planned) ? r.planned : []).filter((c) => !deskQty(r, c.barcode)).length;
}
/** The Book's types, in the Book's own words: ['R'] · ['R','RNC'] · ['N'] · []. */
export const typesOf = (r) => [...new Set((Array.isArray(r?.rows) ? r.rows : []).map((x) => x && x.type).filter(Boolean))];

const colourName = (c) => c?.frame_color || c?.item_name || c?.model_color || bc(c?.barcode);
const STATE_WORD = { btw: 'in between', miss: 'planned, not ordered', nodec: 'ordered without a decision', new: 'new colour', nodata: 'no colour data' };

function tip(code, name, planned, ordered, state) {
  const parts = [code, name].filter(Boolean);
  if (planned != null) parts.push(`${fmt(planned)} planned`);
  if (ordered != null) parts.push(`${fmt(ordered)} ordered`);
  if (STATE_WORD[state]) parts.push(STATE_WORD[state]);
  return parts.join(' · ');
}
const tile = (o) => ({ barcode: bc(o.barcode), modelColor: o.modelColor || bc(o.barcode), itemName: o.itemName || null, imageSmall: o.imageSmall || null,
  state: o.state, qtyPlanned: o.qtyPlanned ?? null, qtyOrdered: o.qtyOrdered ?? null, name: o.name || null, desc: o.desc || null, ref: o.ref || null,
  tooltip: o.tooltip ?? tip(o.modelColor || bc(o.barcode), o.name, o.qtyPlanned, o.qtyOrdered, o.state) });

/** The colour tiles of one row, in the order they render (the prototype's cols + newColours):
 *  WITH an email — the kept colours (in · btw) then the ones a landed PI ordered without a decision;
 *  WITHOUT one — the catalogue's colours (catalog_colours, C6·0b), else the sell sheet's, else one
 *  muted `nodata` tile so the row still reads — worded "No colours kept" when the email decided
 *  every colour out, "No colour data" only when nothing is on record. New colours always come last. */
export function deriveTiles(repeat) {
  const r = repeat || {};
  const hasEmail = !!r.email_model;
  const out = [];
  if (hasEmail) {
    const planned = Array.isArray(r.planned) ? r.planned : [];
    for (const c of planned) {
      const q = deskQty(r, c.barcode);
      const state = c.decision === 'btw' ? 'btw' : (orderedPerColour(r) && !q ? 'miss' : 'plain');
      out.push(tile({ barcode: c.barcode, modelColor: c.model_color, itemName: c.item_name, imageSmall: c.image_small, name: colourName(c), state, qtyPlanned: num(c.qty), qtyOrdered: q }));
    }
    // a colour the Desk ordered that the email never mentions
    const known = new Set([...planned, ...(Array.isArray(r.planned_out) ? r.planned_out : [])].map((c) => bc(c.barcode)));
    const cat = new Map((Array.isArray(r.catalog_colours) ? r.catalog_colours : []).map((c) => [bc(c.barcode), c]));
    for (const d of (Array.isArray(r.desk_colourways) ? r.desk_colourways : [])) {
      if (!d || d.is_option || bc(d.barcode) === '' || known.has(bc(d.barcode)) || !num(d.qty)) continue;
      const c = cat.get(bc(d.barcode)) || {};
      out.push(tile({ barcode: d.barcode, modelColor: c.model_color || d.item_code, itemName: c.item_name, imageSmall: c.image_small, name: colourName(c), state: 'nodec', qtyPlanned: null, qtyOrdered: num(d.qty) }));
    }
  } else {
    const cat = Array.isArray(r.catalog_colours) && r.catalog_colours.length ? r.catalog_colours
      : (Array.isArray(r.sales) ? r.sales : []).filter((s) => s && !s.is_new);
    for (const c of cat) {
      out.push(tile({ barcode: c.barcode, modelColor: c.model_color, itemName: c.item_name, imageSmall: c.image_small, name: colourName(c), state: 'plain', qtyPlanned: null, qtyOrdered: deskQty(r, c.barcode) }));
    }
  }
  for (const [i, nc] of (Array.isArray(r.new_colours) ? r.new_colours : []).entries()) {
    out.push(tile({ barcode: `new-${i}`, modelColor: 'NEW COLOUR', state: 'new', qtyPlanned: num(nc?.qty), desc: nc?.desc || '', ref: nc?.ref || '',
      imageSmall: nc?.image_url || null, tooltip: [nc?.desc, nc?.ref, nc?.qty != null ? `${fmt(num(nc.qty))} planned` : null, 'new colour'].filter(Boolean).join(' · ') }));
  }
  // An email that kept nothing is not "no data": it decided every colour out (14 live rows, STOP A·2).
  const outN = hasEmail && Array.isArray(r.planned_out) ? r.planned_out.length : 0;
  if (!out.length && outN) out.push(tile({ barcode: 'none', modelColor: 'No colours kept', state: 'nodata', desc: `the email decided ${outN === 1 ? 'its colour' : `all ${outN}`} out`, tooltip: `The email kept no colour of this model · ${outN} out` }));
  if (!out.length) out.push(tile({ barcode: 'none', modelColor: 'No colour data', state: 'nodata', desc: 'no colours on record', tooltip: 'No colour data for this model' }));
  return out;
}

/** The six KPI tiles, over the FILTERED list — the prototype's renderStats(). */
export function deriveStats(list) {
  const rows = Array.isArray(list) ? list : [];
  const planned = rows.reduce((s, r) => s + plannedQty(r), 0);
  const ordered = rows.reduce((s, r) => s + n0(r.ordered_qty), 0);
  return {
    repeats: rows.length,
    ordered: rows.filter((r) => r.status === 'ordered').length,
    plannedOnly: rows.filter((r) => r.status === 'planned').length,
    plannedPcs: planned,
    orderedPcs: ordered,
    gap: ordered - planned,
    missingColours: rows.reduce((s, r) => s + missingColours(r), 0),
  };
}

/** The Filters popover's words — the prototype's flag group, verbatim. */
export const FLAG_LABELS = ['No email', 'Colours missing', 'Per-colour ordered', 'PI landed', 'In-between colours'];
export const STATUS_LABELS = { Ordered: 'ordered', 'Planned, not ordered': 'planned', Revived: 'revived' };

/** Everything the popover counts on, per row. */
export function deriveFlags(repeat) {
  const r = repeat || {};
  const tiles = deriveTiles(r);
  const miss = missingColours(r);
  return {
    noEmail: !r.email_model,
    hasMiss: miss > 0,
    missingColours: miss,
    perColourOrdered: orderedPerColour(r),
    piLanded: !!r.desk_pi_no,
    hasBtw: (Array.isArray(r.planned) ? r.planned : []).some((c) => c.decision === 'btw'),
    hasNodec: tiles.some((t) => t.state === 'nodec'),
    hasNew: (Array.isArray(r.new_colours) ? r.new_colours : []).length > 0,
    revived: r.status === 'revived',
    status: r.status || 'planned',
    types: typesOf(r),
    material: r.material || null,
    shape: r.shape || null,
    sub: r.sub_shape || null,
    supplier: r.supplier || null,
  };
}
export function flagHas(repeat, label) {
  const f = deriveFlags(repeat);
  switch (label) {
    case 'No email': return f.noEmail;
    case 'Colours missing': return f.hasMiss;
    case 'Per-colour ordered': return f.perColourOrdered;
    case 'PI landed': return f.piLanded;
    case 'In-between colours': return f.hasBtw;
    default: return true;
  }
}

/** Search over model · name · supplier · colour names · the revived code (the prototype's passes()). */
export function matchesSearch(repeat, q) {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return true;
  const r = repeat || {};
  const colours = [...(r.planned || []), ...(r.planned_out || [])].map(colourName);
  return [r.model, r.name, r.supplier, r.revived_from, ...colours].filter(Boolean).join(' ').toLowerCase().includes(s);
}

/** The popover's seven groups; `all` rides every facet so a row with no value still counts once. */
export function passesFilters(repeat, { q, status, facets } = {}) {
  const r = repeat || {};
  const f = facets || {};
  if (status && status !== 'all' && r.status !== status) return false;   // the seg's own word for no filter
  if (!matchesSearch(r, q)) return false;
  const any = (key, test) => !(f[key] || []).length || (f[key] || []).some(test);
  if (!any('status', (v) => STATUS_LABELS[v] === r.status)) return false;
  if (!any('type', (v) => typesOf(r).includes(v))) return false;
  if (!any('material', (v) => v === r.material)) return false;
  if (!any('shape', (v) => v === r.shape)) return false;
  if (!any('sub', (v) => v === r.sub_shape)) return false;
  if (!any('supplier', (v) => v === r.supplier)) return false;
  if ((f.flag || []).length && !(f.flag || []).every((v) => flagHas(r, v))) return false;   // flags AND, as the prototype
  return true;
}
export const filterRepeatsV2 = (list, state) => (list || []).filter((r) => passesFilters(r, state));

/** The sortable columns (the table's own words) and the default the list opens on. */
export const SORT_KEYS = {
  code: (r) => String(r.model || ''),
  type: (r) => typesOf(r).join('+'),
  material: (r) => String(r.material || ''),
  shape: (r) => `${r.shape || ''} ${r.sub_shape || ''}`,
  base: (r) => num(r.base_price) ?? -1,
  planned: (r) => plannedQty(r) || -1,
  ordered: (r) => n0(r.ordered_qty) || -1,
  status: (r) => String(r.status || ''),
  units: (r) => num(r.units_week) ?? -1,
  gap: (r) => gapOf(r) ?? -1e9,
};
export const TEXT_SORTS = new Set(['code', 'type', 'material', 'shape', 'status', 'supplier']);
export const DEFAULT_SORT = { key: 'ordered', dir: -1 };
/** dir −1 = descending. Ties fall back to units desc (the prototype), then planned desc, then the code. */
export function sortRepeatsV2(list, { key = DEFAULT_SORT.key, dir = DEFAULT_SORT.dir } = {}) {
  const get = SORT_KEYS[key] || SORT_KEYS.ordered;
  return (list || []).slice().sort((a, b) => {
    const x = get(a), y = get(b);
    if (x !== y) return (x > y ? 1 : -1) * dir;
    const u = (num(b.units_week) ?? -1) - (num(a.units_week) ?? -1);
    if (u) return u;
    const p = plannedQty(b) - plannedQty(a);
    if (p) return p;
    return String(a.model || '').localeCompare(String(b.model || ''), 'en');
  });
}
/** 1-based rank → the medal class of the first three (gold · silver · bronze). */
export const medalOf = (rank) => (rank === 1 ? 'g' : rank === 2 ? 's' : rank === 3 ? 'b' : null);

/** The list's lightbox set for one row: the model photo first (when it has one),
 *  then every tile that carries a picture, in the order drawn. `map[i]` is the
 *  item index of tile `i` — null when that tile has no picture to open. */
export function listLightbox(r, tiles) {
  const items = [];
  if (r && r.photo) items.push({ kind: 'model', id: `model:${r.model}`, url: r.photo, barcode: repeatPhotoBarcode(r, r.photo), code: r.model, item_name: r.name || null, frame_colour: null, lens_text: null, qty: null });
  const photoIndex = items.length ? 0 : null;
  const map = (tiles || []).map((t) => {
    if (!t || !t.imageSmall) return null;
    const at = items.length;
    items.push({
      kind: 'repeat', id: t.barcode, url: t.imageSmall, code: t.modelColor, item_name: t.itemName || null,
      planned_qty: t.qtyPlanned, btw: t.state === 'btw', frame_colour: null, lens_text: null, qty: null,
    });
    return at;
  });
  return { items, map, photoIndex: photoIndex != null ? photoIndex : map.find((x) => x != null) ?? 0 };
}

/** The hover-zoom card of one colour tile — the prototype's showZoom over a `.sw`: the full
 *  colour code and the colour's name left, the model's words right. A tile without a picture
 *  answers null, so the zoom stays shut (useHoverZoom opens only on a `url`). */
export function tileZoomCard(r, t) {
  if (!t || !t.imageSmall) return null;
  return { url: t.imageSmall, barcode: realBarcode(t.barcode), code: t.modelColor, name: t.name || null, sub: [r?.name, r?.shape, r?.material].filter(Boolean).join(' · ') };
}

/** The model's picture: its own photo, else the first colour tile's picture (a revived model such as
 *  CL20003 has no model photo but five catalogue colours with one), else null — "No photo". */
export function heroImage(r) {
  if (r && r.photo) return r.photo;
  const t = deriveTiles(r).find((x) => x.imageSmall);
  return t ? t.imageSmall : null;
}

/** `1 colour` · `3 colours` — the ONE plural for every "N colours" string in the list and the drawer. */
export const colourCount = (n) => `${Number(n || 0).toLocaleString('en-US')} colour${Number(n) === 1 ? '' : 's'}`;
